import { useEffect, useMemo, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { Topbar } from '@/components/Topbar';
import { Card, SectionHeader } from '@/components/Card';
import { StatTile } from '@/components/StatTile';
import { DateRangePicker, DateRangeValue } from '@/components/DateRangePicker';
import {
  adsOverviewService, AdsOverviewResponse, AdsOverviewPlatformRow, AdsPlatform,
} from '@/services/ads-overview.service';
import { formatMetaInsightsValue } from '@/utils/currency';
import {
  BarChart3, Megaphone, Music2, Globe, Users, Coins, Trophy, AlertCircle, Info,
  type LucideIcon,
} from 'lucide-react';

// ─── Platform display metadata ────────────────────────────────────────────────

const PLATFORM_META: Record<AdsPlatform, { name: string; icon: LucideIcon; iconClass: string }> = {
  meta:   { name: 'Meta (FB + IG)', icon: Megaphone, iconClass: 'bg-info-soft text-info' },
  tiktok: { name: 'TikTok',         icon: Music2,   iconClass: 'bg-surface-alt text-text-primary' },
  google: { name: 'Google Ads',     icon: Globe,    iconClass: 'bg-warning-soft text-warning' },
};

// ─── Date range resolution ────────────────────────────────────────────────────

function iso(d: Date): string { return d.toISOString().slice(0, 10); }
function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return iso(d);
}

/** Resolve the shared DateRangePicker value into the from/to days the API expects. */
function rangeToFilter(v: DateRangeValue): { from?: string; to?: string } {
  const today = iso(new Date());
  switch (v.preset) {
    case 'today':      return { from: today, to: today };
    case 'yesterday':  return { from: daysAgo(1), to: daysAgo(1) };
    case 'last_7d':    return { from: daysAgo(7), to: today };
    case 'last_14d':   return { from: daysAgo(14), to: today };
    case 'last_30d':   return { from: daysAgo(30), to: today };
    case 'last_90d':   return { from: daysAgo(90), to: today };
    case 'this_month': {
      const d = new Date();
      return { from: iso(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1))), to: today };
    }
    case 'last_month': {
      const d = new Date();
      const first = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() - 1, 1));
      const last = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 0));
      return { from: iso(first), to: iso(last) };
    }
    case 'lifetime':   return { from: '2020-01-01', to: today };
    case 'custom':     return { from: v.since, to: v.until };
    default:           return { from: daysAgo(30), to: today };
  }
}

// ─── Formatting ───────────────────────────────────────────────────────────────

const fmtNum = (n: number) => n.toLocaleString();
const fmtMoney = (n: number | null, currency: string | null): string => {
  if (n === null) return '—';
  if (currency) return formatMetaInsightsValue(String(n), currency);
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
};
const fmtRoas = (n: number | null) => (n === null ? '—' : `${n.toFixed(2)}x`);

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function AdsOverview() {
  const [range, setRange] = useState<DateRangeValue>({ preset: 'last_30d' });
  const [data, setData] = useState<AdsOverviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    adsOverviewService.get(rangeToFilter(range))
      .then((r) => { if (alive) setData(r); })
      .catch((e: unknown) => {
        if (alive) setError(e instanceof Error ? e.message : 'Failed to load ads overview');
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [range]);

  const totals = data?.totals;
  // Totals come back in the company's currency; only a missing rate hides them.
  const mixedCurrencies = !!data && totals?.spend === null
    && data.platforms.some((p) => p.spend > 0);
  const converted = !!totals?.converted && totals.spend !== null;
  const ratesDay = totals?.ratesAsOf ? new Date(totals.ratesAsOf).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : null;

  const chartData = useMemo(
    () => (data?.platforms ?? [])
      .filter((p) => p.dataAvailable)
      .map((p) => ({
        name: PLATFORM_META[p.platform].name,
        // Converted so the bars compare in one currency (falls back to the platform's own).
        Spend: p.spendConverted ?? p.spend,
        'Won value': p.wonValue,
      })),
    [data],
  );

  return (
    <div>
      <Topbar
        title="Ads Overview"
        breadcrumb="Ads / Overview"
        description="Spend, leads, cost-per-lead, and ROAS across Meta, TikTok, and Google — from synced platform data."
        right={<DateRangePicker value={range} onChange={setRange} />}
      />

      <div className="px-7 py-5 space-y-5">
        {error && (
          <Card padded className="border-danger/30 bg-danger-soft/20">
            <div className="flex items-center gap-2 text-sm text-danger">
              <AlertCircle size={15} />
              <span>{error}</span>
            </div>
          </Card>
        )}

        {mixedCurrencies && (
          <Card padded className="border-warning/30 bg-warning-soft/20">
            <div className="flex items-center gap-2 text-xs text-text-secondary">
              <Info size={14} className="shrink-0 text-warning" />
              <span>
                There&apos;s no exchange rate yet for {(totals?.unconverted ?? []).join(', ') || 'one of your ad-account currencies'}, so the cross-platform total is hidden.
                Per-platform numbers below are in each platform&apos;s own currency.
              </span>
            </div>
          </Card>
        )}

        {converted && (
          <div className="flex items-start gap-2 rounded-xl border border-border bg-surface px-4 py-2.5 text-xs text-text-secondary">
            <Info size={14} className="mt-0.5 shrink-0 text-primary" />
            <span>
              Totals are in <b className="font-semibold text-text-primary">{totals?.totalsCurrency}</b>, converted at {ratesDay ? `rates of ${ratesDay}` : 'today’s rates'}.
              Each platform still bills in its own currency — shown in the table below.
            </span>
          </div>
        )}

        {/* KPI cards */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <StatTile
            label="Total spend"
            value={loading ? '…' : mixedCurrencies ? 'Mixed' : fmtMoney(totals?.spend ?? 0, totals?.totalsCurrency ?? null)}
            delta={mixedCurrencies ? 'Missing exchange rate' : converted ? `Converted to ${totals?.totalsCurrency}` : undefined}
            icon={<Coins size={16} />}
            accent="gold"
          />
          <StatTile
            label="Total leads"
            value={loading ? '…' : fmtNum(totals?.leads ?? 0)}
            delta="From ad-sourced lead records"
            icon={<Users size={16} />}
            accent="blue"
          />
          <StatTile
            label="Avg cost / lead"
            value={loading ? '…' : fmtMoney(totals?.costPerLead ?? null, totals?.totalsCurrency ?? null)}
            icon={<BarChart3 size={16} />}
            accent="amber"
          />
          <StatTile
            label="Won value"
            value={loading ? '…' : fmtNum(totals?.wonValue ?? 0)}
            delta={`ROAS ${fmtRoas(totals?.roas ?? null)} · ${fmtNum(totals?.wonDeals ?? 0)} deals won`}
            trend={(totals?.roas ?? 0) >= 1 ? 'up' : 'neutral'}
            icon={<Trophy size={16} />}
            accent="green"
          />
        </div>

        {/* Per-platform comparison table */}
        <Card padded>
          <SectionHeader
            title="Platform comparison"
            subtitle="Leads attributed by lead source; won value joins deals to ad-sourced leads."
            icon={<BarChart3 size={16} />}
          />
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-border text-2xs font-semibold uppercase tracking-wide text-text-muted">
                  <th className="py-2.5 pr-3">Platform</th>
                  <th className="py-2.5 px-3 text-right">Spend</th>
                  <th className="py-2.5 px-3 text-right">Impressions</th>
                  <th className="py-2.5 px-3 text-right">Clicks</th>
                  <th className="py-2.5 px-3 text-right">Leads</th>
                  <th className="py-2.5 px-3 text-right">Cost / lead</th>
                  <th className="py-2.5 px-3 text-right">Won value</th>
                  <th className="py-2.5 pl-3 text-right">ROAS</th>
                </tr>
              </thead>
              <tbody>
                {(data?.platforms ?? []).map((p) => (
                  <PlatformRow key={p.platform} row={p} companyCurrency={totals?.totalsCurrency ?? null} />
                ))}
                {loading && !data && (
                  <tr>
                    <td colSpan={8} className="py-8 text-center text-xs text-text-muted">Loading…</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Spend vs won value chart */}
        <Card padded>
          <SectionHeader
            title="Spend vs won value"
            subtitle={mixedCurrencies
              ? 'Note: bars are in each platform’s own currency — compare shapes, not absolute heights.'
              : totals?.totalsCurrency ? `Per-platform return at a glance, in ${totals.totalsCurrency}.` : 'Per-platform return at a glance.'}
            icon={<Trophy size={16} />}
          />
          {chartData.length === 0 ? (
            <div className="py-12 text-center text-xs text-text-muted">
              No synced ad data in this range yet — connect an ad account and run a report sync.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border, #e5e7eb)" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} width={70}
                  tickFormatter={(v: number) => v.toLocaleString()} />
                <Tooltip formatter={(v) => (typeof v === 'number' ? v.toLocaleString() : String(v ?? ''))} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Spend" fill="#f59e0b" radius={[4, 4, 0, 0]} maxBarSize={48} />
                <Bar dataKey="Won value" fill="#22c55e" radius={[4, 4, 0, 0]} maxBarSize={48} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>
    </div>
  );
}

// ─── Table row ────────────────────────────────────────────────────────────────

function PlatformRow({ row, companyCurrency }: { row: AdsOverviewPlatformRow; companyCurrency: string | null }) {
  const meta = PLATFORM_META[row.platform];
  const Icon = meta.icon;
  return (
    <tr className="border-b border-border/60 last:border-0">
      <td className="py-3 pr-3">
        <div className="flex items-center gap-2.5">
          <span className={`flex h-8 w-8 items-center justify-center rounded-lg ${meta.iconClass}`}>
            <Icon size={15} />
          </span>
          <div>
            <div className="font-semibold text-text-primary">{meta.name}</div>
            {row.currency && row.dataAvailable && (
              <div className="text-2xs text-text-muted">{row.currency}</div>
            )}
          </div>
        </div>
      </td>
      {row.dataAvailable ? (
        <>
          <td className="py-3 px-3 text-right tabular-nums font-medium text-text-primary">
            {fmtMoney(row.spend, row.currency)}
            {row.spendConverted != null && companyCurrency && row.currency && row.currency !== companyCurrency && (
              <div className="text-2xs font-normal text-text-muted">≈ {fmtMoney(row.spendConverted, companyCurrency)}</div>
            )}
          </td>
          <td className="py-3 px-3 text-right tabular-nums text-text-secondary">{fmtNum(row.impressions)}</td>
          <td className="py-3 px-3 text-right tabular-nums text-text-secondary">{fmtNum(row.clicks)}</td>
          <td className="py-3 px-3 text-right tabular-nums font-medium text-text-primary">{fmtNum(row.leads)}</td>
          <td className="py-3 px-3 text-right tabular-nums text-text-secondary">{fmtMoney(row.costPerLead, row.currency)}</td>
          <td className="py-3 px-3 text-right tabular-nums text-text-secondary">
            {companyCurrency ? fmtMoney(row.wonValue, companyCurrency) : fmtNum(row.wonValue)}
            {row.wonDeals > 0 && <span className="ml-1 text-2xs text-text-muted">({row.wonDeals})</span>}
          </td>
          <td className={`py-3 pl-3 text-right tabular-nums font-semibold ${
            row.roas !== null && row.roas >= 1 ? 'text-success' : 'text-text-primary'
          }`}>
            {fmtRoas(row.roas)}
          </td>
        </>
      ) : (
        <td colSpan={7} className="py-3 px-3">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-surface-alt px-2.5 py-1 text-2xs font-medium text-text-muted">
            <Info size={11} />
            No data synced yet
            {row.leads > 0 && <span className="text-text-secondary">· {fmtNum(row.leads)} leads attributed</span>}
          </span>
        </td>
      )}
    </tr>
  );
}
